// Peer Comparison Data - Central Asia & Silk Road Corridor
// The Bloomberg of Central Asia - NATEX Terminal

export interface PeerCountry {
  code: string;
  name: string;
  region: string;
  population: number; // millions
  gdp: number; // billions USD
  gdpGrowth: number; // %
  gdpPerCapita: number; // USD
  inflation: number; // %
  fdiInflows: number; // billions USD
  fdiGrowth: number; // % YoY
  creditRating: string;
  ratingOutlook: 'Positive' | 'Stable' | 'Negative';
  sovereignSpread: number; // bps
  easeOfBusiness: number; // rank out of 190
  corruption: number; // score out of 100
  infraScore: number; // 1-7 scale
  digitalReadiness: number; // 0-100
  laborCost: number; // avg monthly wage USD
  taxRate: number; // corporate %
  // Radar chart metrics (normalized 0-100)
  marketAccess: number;
  stability: number;
  growth: number;
  governance: number;
  infrastructure: number;
  talent: number;
  // Investment signal
  signal: 'STRONG BUY' | 'BUY' | 'HOLD' | 'SELL';
  score: number; // 0-100
}

export interface RatingChange {
  country: string;
  agency: string;
  date: string;
  oldRating: string;
  newRating: string;
  direction: 'upgrade' | 'downgrade' | 'affirm';
}

export interface Precedent {
  country: string;
  period: string;
  catalyst: string;
  gdpBefore: number;
  gdpAfter: number;
  fdiMultiple: number;
  stockReturn: string;
  keyFactors: string[];
  uzbSimilarity: number; // 0-100
}

export interface GrowthDataPoint {
  year: number;
  UZB: number;
  KAZ: number;
  KGZ: number;
  TJK: number;
  TKM: number;
  AZE: number;
  GEO: number;
  MNG: number;
}

// Central Asia & Silk Road Corridor peer countries
export const peerCountries: PeerCountry[] = [
  {
    code: 'UZB',
    name: 'Uzbekistan',
    region: 'Central Asia',
    population: 37.1,
    gdp: 115,
    gdpGrowth: 7.6,
    gdpPerCapita: 3100,
    inflation: 7.5,
    fdiInflows: 11.9,
    fdiGrowth: 53.6,
    creditRating: 'BB',
    ratingOutlook: 'Stable',
    sovereignSpread: 285,
    easeOfBusiness: 69,
    corruption: 31,
    infraScore: 3.8,
    digitalReadiness: 52,
    laborCost: 280,
    taxRate: 15,
    marketAccess: 75,
    stability: 72,
    growth: 88,
    governance: 58,
    infrastructure: 62,
    talent: 70,
    signal: 'STRONG BUY',
    score: 88
  },
  {
    code: 'KAZ',
    name: 'Kazakhstan',
    region: 'Central Asia',
    population: 19.8,
    gdp: 225.0,
    gdpGrowth: 4.5,
    gdpPerCapita: 11364,
    inflation: 8.4,
    fdiInflows: 6.1,
    fdiGrowth: 8.2,
    creditRating: 'BBB-',
    ratingOutlook: 'Stable',
    sovereignSpread: 165,
    easeOfBusiness: 25,
    corruption: 36,
    infraScore: 4.5,
    digitalReadiness: 61,
    laborCost: 580,
    taxRate: 20,
    marketAccess: 70,
    stability: 68,
    growth: 65,
    governance: 55,
    infrastructure: 72,
    talent: 68,
    signal: 'HOLD',
    score: 68
  },
  {
    code: 'KGZ',
    name: 'Kyrgyzstan',
    region: 'Central Asia',
    population: 7.0,
    gdp: 10.5,
    gdpGrowth: 4.8,
    gdpPerCapita: 1500,
    inflation: 10.5,
    fdiInflows: 0.4,
    fdiGrowth: 12.0,
    creditRating: 'B-',
    ratingOutlook: 'Stable',
    sovereignSpread: 520,
    easeOfBusiness: 80,
    corruption: 27,
    infraScore: 2.8,
    digitalReadiness: 42,
    laborCost: 180,
    taxRate: 10,
    marketAccess: 55,
    stability: 45,
    growth: 68,
    governance: 38,
    infrastructure: 42,
    talent: 52,
    signal: 'HOLD',
    score: 52
  },
  {
    code: 'TJK',
    name: 'Tajikistan',
    region: 'Central Asia',
    population: 10.1,
    gdp: 11.5,
    gdpGrowth: 7.2,
    gdpPerCapita: 1140,
    inflation: 4.5,
    fdiInflows: 0.25,
    fdiGrowth: 8.5,
    creditRating: 'B-',
    ratingOutlook: 'Stable',
    sovereignSpread: 580,
    easeOfBusiness: 106,
    corruption: 25,
    infraScore: 2.5,
    digitalReadiness: 28,
    laborCost: 120,
    taxRate: 18,
    marketAccess: 48,
    stability: 52,
    growth: 82,
    governance: 32,
    infrastructure: 35,
    talent: 45,
    signal: 'HOLD',
    score: 48
  },
  {
    code: 'TKM',
    name: 'Turkmenistan',
    region: 'Central Asia',
    population: 6.4,
    gdp: 60.0,
    gdpGrowth: 5.5,
    gdpPerCapita: 9375,
    inflation: 8.0,
    fdiInflows: 1.8,
    fdiGrowth: 5.0,
    creditRating: 'NR',
    ratingOutlook: 'Stable',
    sovereignSpread: 450,
    easeOfBusiness: 154,
    corruption: 19,
    infraScore: 3.2,
    digitalReadiness: 22,
    laborCost: 250,
    taxRate: 8,
    marketAccess: 42,
    stability: 65,
    growth: 72,
    governance: 18,
    infrastructure: 48,
    talent: 35,
    signal: 'HOLD',
    score: 45
  },
  {
    code: 'AZE',
    name: 'Azerbaijan',
    region: 'Caucasus',
    population: 10.2,
    gdp: 78.0,
    gdpGrowth: 2.5,
    gdpPerCapita: 7647,
    inflation: 12.0,
    fdiInflows: 4.5,
    fdiGrowth: 15.0,
    creditRating: 'BB+',
    ratingOutlook: 'Stable',
    sovereignSpread: 210,
    easeOfBusiness: 34,
    corruption: 30,
    infraScore: 4.2,
    digitalReadiness: 58,
    laborCost: 420,
    taxRate: 20,
    marketAccess: 68,
    stability: 62,
    growth: 55,
    governance: 42,
    infrastructure: 65,
    talent: 55,
    signal: 'HOLD',
    score: 62
  },
  {
    code: 'GEO',
    name: 'Georgia',
    region: 'Caucasus',
    population: 3.7,
    gdp: 27.9,
    gdpGrowth: 7.5,
    gdpPerCapita: 7541,
    inflation: 2.8,
    fdiInflows: 1.9,
    fdiGrowth: 18.5,
    creditRating: 'BB',
    ratingOutlook: 'Positive',
    sovereignSpread: 245,
    easeOfBusiness: 7,
    corruption: 56,
    infraScore: 4.0,
    digitalReadiness: 58,
    laborCost: 450,
    taxRate: 15,
    marketAccess: 68,
    stability: 65,
    growth: 78,
    governance: 75,
    infrastructure: 58,
    talent: 62,
    signal: 'BUY',
    score: 76
  },
  {
    code: 'MNG',
    name: 'Mongolia',
    region: 'East Asia',
    population: 3.4,
    gdp: 18.8,
    gdpGrowth: 5.2,
    gdpPerCapita: 5529,
    inflation: 7.2,
    fdiInflows: 2.1,
    fdiGrowth: 22.0,
    creditRating: 'B',
    ratingOutlook: 'Stable',
    sovereignSpread: 425,
    easeOfBusiness: 81,
    corruption: 35,
    infraScore: 3.4,
    digitalReadiness: 48,
    laborCost: 380,
    taxRate: 25,
    marketAccess: 62,
    stability: 58,
    growth: 72,
    governance: 52,
    infrastructure: 48,
    talent: 55,
    signal: 'HOLD',
    score: 58
  }
];

// GDP Growth trajectories - Central Asia focused
export const growthTrajectories: GrowthDataPoint[] = [
  { year: 2019, UZB: 5.7, KAZ: 4.5, KGZ: 4.6, TJK: 7.5, TKM: 6.3, AZE: 2.2, GEO: 5.0, MNG: 5.2 },
  { year: 2020, UZB: 1.9, KAZ: -2.5, KGZ: -8.4, TJK: 4.5, TKM: 5.9, AZE: -4.3, GEO: -6.8, MNG: -4.4 },
  { year: 2021, UZB: 7.4, KAZ: 4.3, KGZ: 6.2, TJK: 9.2, TKM: 6.2, AZE: 5.6, GEO: 10.4, MNG: 1.6 },
  { year: 2022, UZB: 5.7, KAZ: 3.2, KGZ: 7.0, TJK: 8.0, TKM: 6.5, AZE: 4.6, GEO: 10.1, MNG: 5.0 },
  { year: 2023, UZB: 5.9, KAZ: 5.1, KGZ: 6.2, TJK: 8.3, TKM: 6.2, AZE: 1.1, GEO: 7.5, MNG: 7.0 },
  { year: 2024, UZB: 6.3, KAZ: 4.5, KGZ: 4.8, TJK: 7.2, TKM: 5.5, AZE: 2.5, GEO: 7.5, MNG: 5.2 },
  { year: 2025, UZB: 6.5, KAZ: 4.8, KGZ: 5.2, TJK: 7.0, TKM: 5.8, AZE: 3.0, GEO: 6.0, MNG: 5.5 },
];

// Rating changes - Central Asia & Silk Road
export const ratingChanges: RatingChange[] = [
  { country: 'UZB', agency: "S&P", date: '2025-11', oldRating: 'BB-', newRating: 'BB', direction: 'upgrade' },
  { country: 'UZB', agency: 'Fitch', date: '2025-06', oldRating: 'BB-', newRating: 'BB', direction: 'upgrade' },
  { country: 'UZB', agency: "Moody's", date: '2025-06', oldRating: 'Ba3', newRating: 'Ba3', direction: 'affirm' },
  { country: 'KAZ', agency: "S&P", date: '2024-07', oldRating: 'BBB-', newRating: 'BBB-', direction: 'affirm' },
  { country: 'KAZ', agency: "Moody's", date: '2024-04', oldRating: 'Baa2', newRating: 'Baa2', direction: 'affirm' },
  { country: 'KAZ', agency: 'Fitch', date: '2024-01', oldRating: 'BBB', newRating: 'BBB', direction: 'affirm' },
  { country: 'KGZ', agency: "Moody's", date: '2024-05', oldRating: 'B2', newRating: 'B2', direction: 'affirm' },
  { country: 'KGZ', agency: 'Fitch', date: '2023-12', oldRating: 'B-', newRating: 'B-', direction: 'affirm' },
  { country: 'TJK', agency: "Moody's", date: '2024-02', oldRating: 'B3', newRating: 'B3', direction: 'affirm' },
  { country: 'AZE', agency: "S&P", date: '2024-08', oldRating: 'BB+', newRating: 'BB+', direction: 'affirm' },
  { country: 'AZE', agency: 'Fitch', date: '2024-06', oldRating: 'BB+', newRating: 'BB+', direction: 'affirm' },
  { country: 'GEO', agency: 'Fitch', date: '2024-08', oldRating: 'BB-', newRating: 'BB', direction: 'upgrade' },
  { country: 'GEO', agency: "S&P", date: '2024-04', oldRating: 'BB-', newRating: 'BB', direction: 'upgrade' },
  { country: 'MNG', agency: "S&P", date: '2024-06', oldRating: 'B', newRating: 'B', direction: 'affirm' },
  { country: 'MNG', agency: "Moody's", date: '2024-03', oldRating: 'B3', newRating: 'B3', direction: 'affirm' },
];

// Historical precedents relevant to Central Asia
export const precedents: Precedent[] = [
  {
    country: 'Georgia',
    period: '2004-2012',
    catalyst: 'Rose Revolution Reforms',
    gdpBefore: 5.1,
    gdpAfter: 15.8,
    fdiMultiple: 5.1,
    stockReturn: '+185%',
    keyFactors: ['Anti-corruption', 'Flat tax', 'Deregulation', 'Privatization'],
    uzbSimilarity: 82
  },
  {
    country: 'Kazakhstan',
    period: '2000-2008',
    catalyst: 'Oil Boom + Market Reforms',
    gdpBefore: 18.3,
    gdpAfter: 133.4,
    fdiMultiple: 4.8,
    stockReturn: '+520%',
    keyFactors: ['Oil sector development', 'Banking liberalization', 'Infrastructure investment', 'Regional hub strategy'],
    uzbSimilarity: 75
  },
  {
    country: 'Azerbaijan',
    period: '2005-2014',
    catalyst: 'Energy Export Diversification',
    gdpBefore: 12.6,
    gdpAfter: 75.2,
    fdiMultiple: 3.5,
    stockReturn: '+280%',
    keyFactors: ['BTC Pipeline', 'Sovereign wealth fund', 'Non-oil sector push', 'Regional connectivity'],
    uzbSimilarity: 68
  },
  {
    country: 'UAE',
    period: '1990-2008',
    catalyst: 'Diversification + Hub Strategy',
    gdpBefore: 51,
    gdpAfter: 315,
    fdiMultiple: 8.5,
    stockReturn: '+890%',
    keyFactors: ['Free zones', 'Aviation hub', 'Financial center', 'Tourism infrastructure'],
    uzbSimilarity: 58
  },
  {
    country: 'Poland',
    period: '1990-2004',
    catalyst: 'EU Accession Path',
    gdpBefore: 65,
    gdpAfter: 253,
    fdiMultiple: 6.2,
    stockReturn: '+425%',
    keyFactors: ['EU integration', 'Privatization', 'Banking reform', 'Infrastructure investment'],
    uzbSimilarity: 55
  }
];

// Risk-Return scatter data
export const riskReturnData = peerCountries.map(c => ({
  country: c.code,
  name: c.name,
  risk: 100 - c.stability, // Higher stability = lower risk
  return: c.growth,
  size: c.fdiInflows * 10, // Size based on FDI
  signal: c.signal
}));

// Upgrade counts by country - Central Asia focused
export const upgradeStats = {
  UZB: { upgrades: 4, downgrades: 0, affirms: 8, netTrend: '+4', momentum: 'ACCELERATING' },
  KAZ: { upgrades: 1, downgrades: 0, affirms: 10, netTrend: '+1', momentum: 'STABLE' },
  KGZ: { upgrades: 0, downgrades: 0, affirms: 5, netTrend: '0', momentum: 'FLAT' },
  TJK: { upgrades: 0, downgrades: 0, affirms: 4, netTrend: '0', momentum: 'FLAT' },
  TKM: { upgrades: 0, downgrades: 0, affirms: 2, netTrend: '0', momentum: 'NOT RATED' },
  AZE: { upgrades: 1, downgrades: 0, affirms: 8, netTrend: '+1', momentum: 'STABLE' },
  GEO: { upgrades: 4, downgrades: 0, affirms: 6, netTrend: '+4', momentum: 'ACCELERATING' },
  MNG: { upgrades: 1, downgrades: 1, affirms: 6, netTrend: '0', momentum: 'FLAT' }
};

// Summary metrics - Central Asia focused
export const peerSummary = {
  uzbRank: 1,
  totalPeers: 8,
  avgGrowth: 5.4,
  uzbGrowthDelta: '+0.9',
  avgFDI: 2.5,
  uzbFDIDelta: '+0.3',
  bestPerformer: 'UZB',
  worstPerformer: 'TKM',
  upgradeLeaders: ['GEO', 'UZB'],
  riskAdjustedLeader: 'UZB'
};
